import React, { useState, useMemo } from 'react';
import { Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { Subscription, Invoice } from '../../../types/subscriptionManagement';
import {
  avgInvoiceInSubscriptionCurrency,
  totalSpentInSubscriptionCurrency,
} from '../../../utils/subscriptionInvoiceRollup';
import {
  effectiveRenewalDate,
  effectiveRenewalSortTime,
} from '../../../utils/subscriptionRenewal';

interface Props {
  subscriptions: Subscription[];
  invoices: Invoice[];
  onRowClick: (sub: Subscription) => void;
  onEdit: (sub: Subscription) => void;
  onDelete: (sub: Subscription) => void;
}

type SortKey =
  | 'name'
  | 'category'
  | 'owner'
  | 'amount'
  | 'totalSpent'
  | 'monthlyCost'
  | 'renewalDate';

const STATUS_DOT: Record<string, string> = {
  Active: 'bg-emerald-500',
  Paused: 'bg-amber-500',
  Cancelled: 'bg-red-500',
};

const NAME_MAX_LEN = 48;

function truncateWithEllipsis(s: string, max: number): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function currencyPrefix(currency?: string): string {
  if (currency === 'INR') return '₹';
  if (currency === 'GBP') return '£';
  if (currency === 'EUR') return '€';
  return '$';
}

function fmtMoney(
  n: number | string,
  currency?: string,
  maxFractionDigits = 0,
): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const prefix = currencyPrefix(currency);
  return `${prefix}${v.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  })}`;
}

function fmtAmt(n: number | string, currency?: string): string {
  return fmtMoney(n, currency, 0);
}

function fmtDate(d: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const columns: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'owner', label: 'Owner' },
  { key: 'amount', label: 'Approved Amount' },
  { key: 'totalSpent', label: 'Total Spent' },
  { key: 'monthlyCost', label: 'Monthly cost' },
  { key: 'renewalDate', label: 'Renewal' },
];

function invoicesForSub(invoices: Invoice[], subId: string) {
  return invoices.filter(i => i.subscriptionId === subId);
}

export const SubscriptionTable: React.FC<Props> = ({
  subscriptions,
  invoices,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = useMemo(() => {
    return [...subscriptions].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'amount') {
        cmp = (Number(a.amount) || 0) - (Number(b.amount) || 0);
      } else if (sortKey === 'totalSpent') {
        const ta = totalSpentInSubscriptionCurrency(
          invoicesForSub(invoices, a.id),
          a.currency || 'USD',
        );
        const tb = totalSpentInSubscriptionCurrency(
          invoicesForSub(invoices, b.id),
          b.currency || 'USD',
        );
        cmp = ta - tb;
      } else if (sortKey === 'monthlyCost') {
        const aa = avgInvoiceInSubscriptionCurrency(
          invoicesForSub(invoices, a.id),
          a.currency || 'USD',
        );
        const ab = avgInvoiceInSubscriptionCurrency(
          invoicesForSub(invoices, b.id),
          b.currency || 'USD',
        );
        cmp = aa - ab;
      } else if (sortKey === 'renewalDate') {
        cmp =
          effectiveRenewalSortTime(a, invoices) -
          effectiveRenewalSortTime(b, invoices);
      } else {
        const va = String((a as unknown as Record<string, unknown>)[sortKey] ?? '').toLowerCase();
        const vb = String((b as unknown as Record<string, unknown>)[sortKey] ?? '').toLowerCase();
        cmp = va.localeCompare(vb);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [subscriptions, invoices, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  if (subscriptions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
        <p className="text-slate-500 text-sm">No subscriptions yet. Click "Add Subscription" to get started.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 cursor-pointer select-none hover:text-slate-700 transition-colors ${
                    col.key === 'name' ? 'min-w-[18rem] max-w-[26rem] w-[22rem]' : ''
                  }`}
                  onClick={() => toggleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key && (
                      sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                    )}
                  </div>
                </th>
              ))}
              <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 w-20">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(sub => {
              const subInv = invoicesForSub(invoices, sub.id);
              const spent = totalSpentInSubscriptionCurrency(subInv, sub.currency || 'USD');
              const avgMo = avgInvoiceInSubscriptionCurrency(subInv, sub.currency || 'USD');
              return (
              <tr
                key={sub.id}
                onClick={() => onRowClick(sub)}
                className="border-b border-slate-50 hover:bg-blue-50/40 cursor-pointer transition-colors group"
              >
                <td className="px-3 py-2.5 min-w-[18rem] max-w-[26rem] w-[22rem]">
                  <div className="flex items-start gap-2 min-w-0">
                    <span
                      role="img"
                      aria-label={sub.status ? `Status: ${sub.status}` : 'Status unknown'}
                      className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${STATUS_DOT[sub.status] || 'bg-slate-400'}`}
                      title={sub.status || undefined}
                    />
                    <div className="min-w-0">
                      <p
                        className="text-[13px] font-semibold text-slate-800"
                        title={sub.name || undefined}
                      >
                        {sub.name
                          ? truncateWithEllipsis(sub.name, NAME_MAX_LEN)
                          : '—'}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate" title={sub.vendor || undefined}>
                        {sub.vendor || '—'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <p className="text-[12px] text-slate-700">{sub.category || '—'}</p>
                  <p
                    className="text-[11px] text-slate-500 truncate max-w-[10rem]"
                    title={sub.department || undefined}
                  >
                    {sub.department || '—'}
                  </p>
                </td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600">{sub.owner || '—'}</td>
                <td className="px-3 py-2.5 text-[12px] font-medium text-slate-700">
                  {fmtAmt(sub.amount, sub.currency)}
                  {sub.billingCycle === 'Annual' && <span className="text-[10px] text-slate-400 ml-0.5">/yr</span>}
                  {sub.billingCycle === 'Monthly' && <span className="text-[10px] text-slate-400 ml-0.5">/mo</span>}
                  {sub.billingCycle === 'Quarterly' && <span className="text-[10px] text-slate-400 ml-0.5">/qtr</span>}
                </td>
                <td className="px-3 py-2.5 text-[12px] font-medium text-slate-700 whitespace-nowrap">
                  {fmtMoney(spent, sub.currency, 2)}
                </td>
                <td className="px-3 py-2.5 text-[12px] font-medium text-slate-700 whitespace-nowrap">
                  {subInv.length ? (
                    <>
                      {fmtMoney(avgMo, sub.currency, 2)}
                      <span className="text-[10px] text-slate-400 ml-0.5">/mo</span>
                    </>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-slate-600">
                  {fmtDate(effectiveRenewalDate(sub, invoices))}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); onEdit(sub); }}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); onDelete(sub); }}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
